/**
 * Stripe / PayPal sandbox detection and test credential display helpers.
 */

export const STRIPE_SANDBOX_TEST_CARD = {
  number: '4242 4242 4242 4242',
  expiry: '12/34',
  cvc: '123',
};

/**
 * PayPal sandbox buyer — filled from /api/payment/config (PAYPAL_SANDBOX_BUYER_* env).
 * Do not hardcode passwords in the frontend bundle.
 */
export const PAYPAL_SANDBOX_BUYER = {
  email: '',
  password: '',
};

export function resolvePayPalSandboxBuyer(config) {
  const fromConfig = config?.paypalSandboxBuyer;
  if (fromConfig?.email || fromConfig?.password) {
    return {
      email: fromConfig.email || '',
      password: fromConfig.password || '',
    };
  }
  return PAYPAL_SANDBOX_BUYER;
}

export const ADMIN_STRIPE_MODE_KEY = 'adminStripePaymentMode';
export const ADMIN_PAYPAL_MODE_KEY = 'adminPayPalPaymentMode';
export const ADMIN_STRIPE_MODE_EXPLICIT_KEY = 'adminStripePaymentModeExplicit';
export const ADMIN_PAYPAL_MODE_EXPLICIT_KEY = 'adminPayPalPaymentModeExplicit';
/** @deprecated Legacy combined key — no longer applied automatically */
export const ADMIN_PAYMENT_MODE_KEY = 'adminPaymentMode';

export function getStoredAdminStripeMode() {
  if (typeof window === 'undefined') return null;
  if (sessionStorage.getItem(ADMIN_STRIPE_MODE_EXPLICIT_KEY) !== '1') return null;
  const mode = sessionStorage.getItem(ADMIN_STRIPE_MODE_KEY);
  return mode === 'live' || mode === 'sandbox' ? mode : null;
}

export function getStoredAdminPayPalMode() {
  if (typeof window === 'undefined') return null;
  if (sessionStorage.getItem(ADMIN_PAYPAL_MODE_EXPLICIT_KEY) !== '1') return null;
  const mode = sessionStorage.getItem(ADMIN_PAYPAL_MODE_KEY);
  return mode === 'live' || mode === 'sandbox' ? mode : null;
}

/** @deprecated Use getStoredAdminStripeMode / getStoredAdminPayPalMode */
export function getStoredAdminPaymentMode() {
  return getStoredAdminStripeMode();
}

export function setStoredAdminStripeMode(mode, { explicit = true } = {}) {
  if (typeof window === 'undefined') return;
  if (mode === 'live' || mode === 'sandbox') {
    sessionStorage.setItem(ADMIN_STRIPE_MODE_KEY, mode);
    if (explicit) {
      sessionStorage.setItem(ADMIN_STRIPE_MODE_EXPLICIT_KEY, '1');
    }
  }
}

export function setStoredAdminPayPalMode(mode, { explicit = true } = {}) {
  if (typeof window === 'undefined') return;
  if (mode === 'live' || mode === 'sandbox') {
    sessionStorage.setItem(ADMIN_PAYPAL_MODE_KEY, mode);
    if (explicit) {
      sessionStorage.setItem(ADMIN_PAYPAL_MODE_EXPLICIT_KEY, '1');
    }
  }
}

export function clearStoredAdminStripeMode() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(ADMIN_STRIPE_MODE_KEY);
  sessionStorage.removeItem(ADMIN_STRIPE_MODE_EXPLICIT_KEY);
}

export function clearStoredAdminPayPalMode() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(ADMIN_PAYPAL_MODE_KEY);
  sessionStorage.removeItem(ADMIN_PAYPAL_MODE_EXPLICIT_KEY);
}

/** @deprecated */
export function setStoredAdminPaymentMode(mode) {
  setStoredAdminStripeMode(mode);
  setStoredAdminPayPalMode(mode);
}

export function clearStoredAdminPaymentModes() {
  if (typeof window === 'undefined') return;
  clearStoredAdminStripeMode();
  clearStoredAdminPayPalMode();
  sessionStorage.removeItem(ADMIN_PAYMENT_MODE_KEY);
}

/** Drop stale session overrides from before per-provider explicit flags. */
export function clearLegacyPaymentModeStorage() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(ADMIN_PAYMENT_MODE_KEY);
  if (sessionStorage.getItem(ADMIN_STRIPE_MODE_EXPLICIT_KEY) !== '1') {
    sessionStorage.removeItem(ADMIN_STRIPE_MODE_KEY);
  }
  if (sessionStorage.getItem(ADMIN_PAYPAL_MODE_EXPLICIT_KEY) !== '1') {
    sessionStorage.removeItem(ADMIN_PAYPAL_MODE_KEY);
  }
}

/**
 * Build payment-mode headers for admins.
 * Prefer explicit mode args (React state); fall back to sessionStorage.
 */
export function buildPaymentModeHeaders(userIsAdmin = false, modes = {}) {
  if (!userIsAdmin) return {};
  const headers = {};
  const stripeMode =
    (modes.stripeMode === 'live' || modes.stripeMode === 'sandbox'
      ? modes.stripeMode
      : null) || getStoredAdminStripeMode();
  const paypalMode =
    (modes.paypalMode === 'live' || modes.paypalMode === 'sandbox'
      ? modes.paypalMode
      : null) || getStoredAdminPayPalMode();
  if (stripeMode) headers['X-Stripe-Payment-Mode'] = stripeMode;
  if (paypalMode) headers['X-PayPal-Payment-Mode'] = paypalMode;
  return headers;
}

export function isLocalCheckoutHost() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

export function resolvePaymentSandboxFlags(config) {
  if (!config) {
    return { isStripeSandbox: false, isPayPalSandbox: false, isPaymentSandbox: false };
  }

  const isStripeSandbox =
    config.stripeSandbox === true ||
    config.stripeEnvironment === 'sandbox' ||
    (config.stripePublishableKey || '').includes('_test_');

  const isPayPalSandbox =
    config.paypalSandbox === true ||
    config.paypalEnvironment === 'sandbox';

  return {
    isStripeSandbox,
    isPayPalSandbox,
    isPaymentSandbox: isStripeSandbox || isPayPalSandbox,
  };
}

/** Show fake card / sandbox login on checkout when using test provider accounts locally (or for admins). */
export function shouldShowSandboxCredentials(config, userIsAdmin = false) {
  const { isStripeSandbox, isPayPalSandbox } = resolvePaymentSandboxFlags(config);
  return (isStripeSandbox || isPayPalSandbox) && (isLocalCheckoutHost() || userIsAdmin);
}

export async function fetchPaymentSandboxFlags(options = {}) {
  try {
    const headers = {
      ...(options.headers || {}),
      ...buildPaymentModeHeaders(options.userIsAdmin),
    };
    const res = await fetch('/api/payment/config', {
      cache: 'no-store',
      headers,
    });
    if (!res.ok) return resolvePaymentSandboxFlags(null);
    const config = await res.json();
    return { ...resolvePaymentSandboxFlags(config), config };
  } catch {
    return { ...resolvePaymentSandboxFlags(null), config: null };
  }
}

/**
 * Infer sandbox vs live from Stripe Checkout session id or API mode fields.
 * Prefer this over server default config on the success page.
 */
export function inferCheckoutPaymentMode({
  sessionId,
  provider,
  stripeMode,
  paypalMode,
  paymentMode,
} = {}) {
  const sid = String(sessionId || '');
  if (sid.startsWith('cs_live_')) return 'live';
  if (sid.startsWith('cs_test_')) return 'sandbox';

  const fromApi =
    (provider === 'paypal'
      ? normalizeMode(paypalMode || paymentMode)
      : normalizeMode(stripeMode || paymentMode)) ||
    normalizeMode(paymentMode);
  if (fromApi) return fromApi;

  return null;
}

function normalizeMode(mode) {
  const m = String(mode || '').trim().toLowerCase();
  if (m === 'live' || m === 'production') return 'live';
  if (m === 'sandbox' || m === 'test') return 'sandbox';
  return null;
}

export function providerModesAvailable(availableProviderModes) {
  if (!availableProviderModes) return false;
  const { stripe, paypal } = availableProviderModes;
  return Boolean(
    stripe?.sandbox || stripe?.live || paypal?.sandbox || paypal?.live
  );
}

/** Map API config to per-provider availability shape (merge new + legacy flags). */
export function normalizeAvailableProviderModes(config) {
  const fromNew = config?.availableProviderModes;
  const legacy = config?.availablePaymentModes;
  if (!fromNew && !legacy) return null;

  return {
    stripe: {
      sandbox: Boolean(fromNew?.stripe?.sandbox || legacy?.sandbox?.stripe),
      live: Boolean(fromNew?.stripe?.live || legacy?.live?.stripe),
    },
    paypal: {
      sandbox: Boolean(fromNew?.paypal?.sandbox || legacy?.sandbox?.paypal),
      live: Boolean(fromNew?.paypal?.live || legacy?.live?.paypal),
    },
  };
}
